import { useMachine } from '@xstate/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import cx from 'classnames'
import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { MetaTags } from 'components/MetaTags'
import { useAuth } from 'hooks/UseAuth'
import { useBrand } from 'hooks/useBrand'
import { useQueryParams } from 'hooks/useQueryParams'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import {
  COMPANY_DATA_PATH,
  CREATE_ACCOUNT_PATH,
  CREATE_WORKSPACE_PATH,
  DEMO_SCHEDULED_PATH,
  LOGIN_OR_SIGNUP_PATH,
  SCHEDULE_DEMO_PATH,
  SIGNUP_PATH,
} from 'paths'
import { getAlmanacUrlFor } from 'utils/urlUtils'
import { useWorkspaceInviteToken } from 'utils/workspaceUtils'
import Styles from './LoginOrSignup.module.scss'
import { NewPaywallModal } from 'components/Paywall/NewPaywallModal/NewPaywallModal'
import CheckYourInboxForPasswordResetEmail from './components/CheckYourInboxForPasswordResetEmail/CheckYourInboxForPasswordResetEmail'
import CompanyData from './components/CompanyData/CompanyData'
import ContinueWithEmailOrGmail from './components/ContinueWithEmailOrGmail/ContinueWithEmailOrGmail'
import CreateAccount from './components/CreateAccount/CreateAccount'
import CreateWorkspace from './components/CreateWorkspace/CreateWorkspace'
import { DemoScheduled } from './components/DemoScheduled/DemoScheduled'
import EmailVerification from './components/EmailVerification/EmailVerification'
import EnterPassword from './components/EnterPassword/EnterPassword'
import blazeHeroText from './components/OnboardingLoader/blazeHeroText.png'
import ResetPassword from './components/ResetPassword/ResetPassword'
import SamlSsoLogin from './components/SamlSsoLogin/SamlSsoLogin'
import { ScheduleDemo } from './components/ScheduleDemo/ScheduleDemo'
import { SkeletonLoader } from './components/SkeletonLoader/SkeletonLoader'
import { WaitingList } from './components/WaitingList/WaitingList'
import { useRegistrationChannel } from './hooks/useRegistrationChannel'
import { loginOrSignupMachine } from './loginOrSignupMachine'
import { RegistrationType, descriptionTag, titleTag, totalCompletableSteps, trackRegistrationStart } from './utils'
import { Explanation } from './components/Blaze/StartFreeTrial/components/Explanation/Explanation'
import { Plan } from './components/Blaze/StartFreeTrial/components/Plan/Plan'
import CheckEmailOrEnterVerificationCode from './components/CheckEmailOrEnterVerificationCode/CheckEmailOrEnterVerificationCode'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Spinner } from 'common/Spinner/Spinner'
import { useAutopilotProvider } from 'hooks/useAutopilotProvider'
import { AuthChannelProvider } from 'providers/AuthChannelProvider/AuthChannelProvider'

type InitialPage =
  | 'continueWithEmailOrGmail'
  | 'emailVerification'
  | 'createWorkspace'
  | 'createAccount'
  | 'scheduleDemoLoader'
  | 'blazeStartFreeTrial'
  | 'scheduleDemo'
  | 'demoScheduled'
  | 'companyData'

const LoginOrSignup = observer(() => {
  useAutopilotProvider()

  const auth = useAuth()
  const { featureFlags } = useRootStore()
  const newRegistrationEnabled = useFlipperFlag('new_registration')

  const history = useHistory()
  const location = useLocation()
  const workspaceInviteToken = useWorkspaceInviteToken()
  const { isTabletPortrait } = useResponsiveDesign()
  const brand = useBrand()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const {
    registration_verification_token: verificationTokenParam,
    reset_password_token: resetPasswordTokenParam,
    email: emailParam,
    redirect_to: redirectToParam,
    inviter,
    registration_type: registrationTypeParam,
    initial_page: overrideInitialPage,
    promotion_code: promotionCode,
    new_registration_override: newRegistrationOverride,
  } = useQueryParams()
  const [email, setEmail] = useState(emailParam)
  const docRedirect = !!redirectToParam ? decodeURIComponent(redirectToParam).startsWith('/docs/') : false
  const isInvitedUser = !!workspaceInviteToken || !!inviter
  const [totalSteps, setTotalSteps] = useState(0)

  const isNewRegistration = newRegistrationEnabled || newRegistrationOverride === 'true'

  useEffect(() => {
    if (!isInvitedUser) {
      setTotalSteps(totalCompletableSteps)
    }
  }, [isTabletPortrait, isInvitedUser])

  let initialPage: InitialPage = 'continueWithEmailOrGmail'
  if (verificationTokenParam && !auth?.first_name) initialPage = 'emailVerification'

  const replaceWindowStateAndSetInitialPage = (page: InitialPage) => {
    const { state, search } = location

    // eslint-disable-next-line no-restricted-properties
    window.history.replaceState(state, '', `${SIGNUP_PATH}${search}`)

    initialPage = page
  }
  if (location.pathname.includes(CREATE_WORKSPACE_PATH)) {
    replaceWindowStateAndSetInitialPage('createWorkspace')
  }

  if (location.pathname.includes(CREATE_ACCOUNT_PATH)) {
    !!email?.length
      ? replaceWindowStateAndSetInitialPage('createAccount')
      : replaceWindowStateAndSetInitialPage('continueWithEmailOrGmail')
  }

  if (location.pathname.includes(SCHEDULE_DEMO_PATH)) {
    replaceWindowStateAndSetInitialPage('scheduleDemoLoader')
  }

  if (location.pathname.includes(DEMO_SCHEDULED_PATH)) {
    replaceWindowStateAndSetInitialPage('demoScheduled')
  }

  if (location.pathname.includes(COMPANY_DATA_PATH)) {
    replaceWindowStateAndSetInitialPage('companyData')
  }

  if (overrideInitialPage) initialPage = overrideInitialPage
  const [currentPage, send] = useMachine(loginOrSignupMachine({ initial: initialPage }))

  const [registrationType, setRegistrationType] = useState<RegistrationType>(
    registrationTypeParam || RegistrationType.Unknown,
  )
  const [emailVerificationCode, setEmailVerificationCode] = useState<string | undefined>()
  const [workspaceSeeded, setWorkspaceSeeded] = useState<boolean>(false)
  const [, setOnboardingDocPath] = useState<string | undefined>()
  const [fetchingDemoState, setFetchingDemoState] = useState(false)
  const [newlyCreatedWorkspaceId, setNewlyCreatedWorkspaceId] = useState<number | undefined>()

  useRegistrationChannel({ setOnboardingDocPath, setWorkspaceSeeded })

  const transitionPage = useCallback(
    (page: string) => {
      send(page)
    },
    [send],
  )

  const trackRegistrationStarted = useCallback(() => {
    if (inviter) {
      setRegistrationType(RegistrationType.Invited)
      trackRegistrationStart(RegistrationType.Invited)
    } else {
      setRegistrationType(RegistrationType.EmailAndPassword)
      trackRegistrationStart(RegistrationType.EmailAndPassword)
    }
  }, [inviter, setRegistrationType])

  useEffect(() => {
    const fetchDemoState = async () => {
      if (fetchingDemoState) return

      setFetchingDemoState(true)
      const res = await UserClient.getRegistrationData()

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        const { user_registration_data } = res.data

        if (!!user_registration_data?.demo_completed_at) {
          transitionPage('INVITE_TEAM')
        } else if (!!user_registration_data?.demo_scheduled_at) {
          transitionPage('DEMO_SCHEDULED')
        } else {
          transitionPage('SCHEDULE_DEMO')
        }
        setFetchingDemoState(false)
      }
    }

    if (currentPage.matches('scheduleDemoLoader')) fetchDemoState()
  }, [location.pathname, history, initialPage, currentPage, fetchingDemoState, transitionPage])

  useEffect(() => {
    const redirectToPasswordReset = () => {
      if (auth && !resetPasswordTokenParam) return
      if (!currentPage.matches('continueWithEmailOrGmail')) return

      if (resetPasswordTokenParam) {
        transitionPage('RESET_PASSWORD')
      }
    }

    redirectToPasswordReset()
  }, [resetPasswordTokenParam, transitionPage, currentPage, auth])

  // Redirect already authenticated users to their intended destination
  useEffect(() => {
    if (auth && redirectToParam) {
      // Extract pathname if redirectToParam is a full URL
      try {
        // eslint-disable-next-line no-restricted-properties
        const url = new URL(redirectToParam, window.location.origin)
        history.push(url.pathname + url.search + url.hash)
      } catch {
        // If it's not a valid URL, treat it as a path
        history.push(redirectToParam)
      }
    }
  }, [auth, redirectToParam, history])

  useEffect(() => {
    document.getElementsByTagName('body')[0].style.background = 'unset'

    const wrapper = wrapperRef.current
    const eventListener = () => wrapperRef.current?.scrollTo(0, 0)
    wrapper?.addEventListener('scrollTop', eventListener)

    return () => wrapper?.removeEventListener('scrollTop', eventListener)
  }, [])

  useEffect(() => {
    wrapperRef.current?.scrollTo?.(0, 0)
  }, [currentPage])

  const isContinueWithEmailOrGmail =
    currentPage.matches('continueWithEmailOrGmail') ||
    currentPage.matches('usingGmail') ||
    currentPage.matches('usingEmailAndPassword')

  if (currentPage.matches('checkEmailOrEnterVerificationCode') && isNewRegistration) {
    const allQueryParams = {
      email,
      registration_verification_token: verificationTokenParam,
      reset_password_token: resetPasswordTokenParam,
      redirect_to: redirectToParam,
      inviter,
      registration_type: registrationTypeParam,
      initial_page: overrideInitialPage,
      promotion_code: promotionCode,
    }

    // Build query string while filtering out undefined values
    const queryString = Object.entries(allQueryParams)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&')

    return <Redirect to={`${SIGNUP_PATH}?${queryString}`} />
  }

  if (!featureFlags.ready) {
    featureFlags.load()

    return (
      <div className={Styles.spinnerContainer}>
        <Spinner />
      </div>
    )
  }

  return (
    <div
      className={cx(Styles.wrapper, {
        [Styles.verify]: currentPage.matches('checkEmailOrEnterVerificationCode'),
      })}
      data-theme="light"
      ref={wrapperRef}
    >
      <Helmet>
        <link rel="canonical" href={getAlmanacUrlFor('login_or_signup')} />
        <script src="https://apis.google.com/js/platform.js?onload=init" async defer></script>
      </Helmet>
      <MetaTags
        url={LOGIN_OR_SIGNUP_PATH}
        title={titleTag(docRedirect, brand)}
        description={descriptionTag(docRedirect, brand)}
      />
      <img src={blazeHeroText} style={{ display: 'none' }} alt="Preloaded Hero" />
      <div className={Styles.content}>
        {isContinueWithEmailOrGmail && (
          <AuthChannelProvider>
            <ContinueWithEmailOrGmail
              emailParam={emailParam}
              setEmail={setEmail}
              transitionPage={transitionPage}
              workspaceInviteToken={workspaceInviteToken}
              redirectTo={redirectToParam || ''}
            />
          </AuthChannelProvider>
        )}
        {currentPage.matches('usingSamlSso') && (
          <SamlSsoLogin
            setEmail={setEmail}
            email={email}
            transitionPage={transitionPage}
            redirectTo={redirectToParam || ''}
          />
        )}
        {currentPage.matches('checkEmailOrEnterVerificationCode') && (
          <CheckEmailOrEnterVerificationCode
            email={email}
            totalSteps={totalSteps}
            transitionPage={transitionPage}
            setEmailVerificationCode={setEmailVerificationCode}
          />
        )}

        {currentPage.matches('enterPassword') && (
          <EnterPassword
            email={email}
            transitionPage={transitionPage}
            redirectTo={redirectToParam || ''}
            workspaceInviteToken={workspaceInviteToken}
          />
        )}
        {currentPage.matches('checkYourInboxForPasswordResetEmail') && (
          <CheckYourInboxForPasswordResetEmail
            email={email}
            transitionPage={transitionPage}
            workspaceInviteToken={workspaceInviteToken}
          />
        )}
        {currentPage.matches('emailVerification') && (
          <EmailVerification token={verificationTokenParam} transitionPage={transitionPage} />
        )}
        {currentPage.matches('resetPassword') && (
          <ResetPassword
            token={resetPasswordTokenParam}
            email={emailParam}
            transitionPage={transitionPage}
            redirectTo={redirectToParam || ''}
            workspaceInviteToken={workspaceInviteToken}
          />
        )}
        {currentPage.matches('createAccount') && (
          <CreateAccount
            email={email}
            trackRegistrationStarted={trackRegistrationStarted}
            token={verificationTokenParam}
            transitionPage={transitionPage}
            redirectTo={redirectToParam || ''}
            workspaceInviteToken={workspaceInviteToken}
            emailVerificationCode={emailVerificationCode}
            totalSteps={totalSteps}
          />
        )}
        {currentPage.matches('companyData') && (
          <CompanyData
            workspaceSeeded={workspaceSeeded}
            isInvitedUser={isInvitedUser}
            registrationType={registrationType}
            transitionPage={transitionPage}
            redirectTo={redirectToParam || ''}
            totalSteps={totalSteps}
            onWorkspaceLoaded={(id) => {
              if (!newlyCreatedWorkspaceId) setNewlyCreatedWorkspaceId(id)
            }}
          />
        )}

        {currentPage.matches('blazeStartFreeTrial') && newlyCreatedWorkspaceId && <NewPaywallModal />}
        {currentPage.matches('explanation') && <Explanation totalSteps={totalSteps} transitionPage={transitionPage} />}
        {currentPage.matches('plan') && <Plan totalSteps={totalSteps} transitionPage={transitionPage} />}
        {currentPage.matches('createWorkspace') && (
          <CreateWorkspace
            transitionPage={transitionPage}
            isInvitedUser={isInvitedUser}
            setNewlyCreatedWorkspaceId={setNewlyCreatedWorkspaceId}
          />
        )}
        {currentPage.matches('scheduleDemoLoader') && <SkeletonLoader />}
        {currentPage.matches('scheduleDemo') && (
          <ScheduleDemo transitionPage={transitionPage} totalSteps={totalSteps} />
        )}
        {currentPage.matches('demoScheduled') && (
          <DemoScheduled transitionPage={transitionPage} totalSteps={totalSteps} />
        )}
        {currentPage.matches('waitingList') && <WaitingList totalSteps={totalSteps} transitionPage={transitionPage} />}
        <span></span>
      </div>
    </div>
  )
})

export default LoginOrSignup
